import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './Analytics.css';
import { getAgentAnalytics } from '../../../api/services/analyticsService';

const safeNumber = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);

const AgentAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await getAgentAnalytics();
      setData(resp?.data || null);
    } catch (e) {
      setError(e?.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const derived = useMemo(() => {
    const d = data || {};
    return {
      activeAssignmentsCount: safeNumber(d.activeAssignmentsCount),
      propertiesManagedCount: safeNumber(d.propertiesManagedCount),
      maintenanceOpenCount: safeNumber(d.maintenanceOpenCount),
      maintenanceHandledCount: safeNumber(d.maintenanceHandledCount),
      maintenanceCompletedCount: safeNumber(d.maintenanceCompletedCount),
      maintenanceCancelledCount: safeNumber(d.maintenanceCancelledCount),
    };
  }, [data]);

  const maintenanceTotal =
    derived.maintenanceOpenCount +
    derived.maintenanceHandledCount +
    derived.maintenanceCompletedCount +
    derived.maintenanceCancelledCount;

  return (
    <div className="agent-analytics-container">
      <div className="agent-analytics-header">
        <h1 className="page-title">Analytics</h1>
      </div>

      {error ? (
        <div className="agent-analytics-alert agent-analytics-alert-error">
          <div className="agent-analytics-alert-title">Couldn’t load analytics</div>
          <div className="agent-analytics-alert-message">{error}</div>
          <button className="agent-analytics-retry-btn" onClick={fetchAnalytics}>
            Retry
          </button>
        </div>
      ) : null}

      <div className="metrics-row">
        <div className={`metric-card ${loading ? 'metric-card-skeleton' : ''}`}>
          <div className="metric-icon metric-icon-house">📌</div>
          <div className="metric-content">
            <div className="metric-label">Active Assignments</div>
            <div className="metric-value">{derived.activeAssignmentsCount}</div>
            <div className="metric-trend metric-trend-neutral">
              <span className="trend-text">Currently active</span>
            </div>
          </div>
        </div>

        <div className={`metric-card ${loading ? 'metric-card-skeleton' : ''}`}>
          <div className="metric-icon metric-icon-check">🏢</div>
          <div className="metric-content">
            <div className="metric-label">Properties Managed</div>
            <div className="metric-value">{derived.propertiesManagedCount}</div>
            <div className="metric-trend metric-trend-neutral">
              <span className="trend-text">Assigned properties</span>
            </div>
          </div>
        </div>

        <div className={`metric-card ${loading ? 'metric-card-skeleton' : ''}`}>
          <div className="metric-icon metric-icon-dollar-warning">🔧</div>
          <div className="metric-content">
            <div className="metric-label">Maintenance Open</div>
            <div className="metric-value">{derived.maintenanceOpenCount}</div>
            <div className={`metric-trend ${derived.maintenanceOpenCount > 0 ? 'metric-trend-warning' : 'metric-trend-neutral'}`}>
              <span className="trend-text">{derived.maintenanceOpenCount > 0 ? 'Needs attention' : 'All clear'}</span>
            </div>
          </div>
        </div>

        <div className={`metric-card ${loading ? 'metric-card-skeleton' : ''}`}>
          <div className="metric-icon metric-icon-house">✅</div>
          <div className="metric-content">
            <div className="metric-label">Maintenance Completed</div>
            <div className="metric-value">{derived.maintenanceCompletedCount}</div>
            <div className="metric-trend metric-trend-neutral">
              <span className="trend-text">Completed jobs</span>
            </div>
          </div>
        </div>
      </div>

      <div className="charts-row">
        <div className="chart-card full-width">
          <h3 className="chart-title">Maintenance Breakdown</h3>
          <p className="chart-subtitle">Status summary</p>

          <div className="agent-maintenance-grid">
            <div className="agent-maintenance-item">
              <span className="agent-maintenance-label">Open</span>
              <span className="agent-maintenance-value">{derived.maintenanceOpenCount}</span>
            </div>
            <div className="agent-maintenance-item">
              <span className="agent-maintenance-label">Handled</span>
              <span className="agent-maintenance-value">{derived.maintenanceHandledCount}</span>
            </div>
            <div className="agent-maintenance-item">
              <span className="agent-maintenance-label">Completed</span>
              <span className="agent-maintenance-value">{derived.maintenanceCompletedCount}</span>
            </div>
            <div className="agent-maintenance-item">
              <span className="agent-maintenance-label">Cancelled</span>
              <span className="agent-maintenance-value">{derived.maintenanceCancelledCount}</span>
            </div>
          </div>

          <div className="agent-analytics-muted">
            {maintenanceTotal > 0 ? `${maintenanceTotal} total maintenance items` : 'No maintenance activity yet.'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentAnalytics;

